import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {collectEnumValues} from '../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {Layer} from '../../../../libs/shared/ui/src/components/layers/layer';
import {Anchor} from '../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {RepositionOverlay} from '../../../../libs/shared/ui/src/components/reposition/repositionOverlay';
import {getHighlightPulseCss} from '../../../../libs/shared/ui/src/styles/mixins';
import {OnboardingTipTypesEnum} from '../../../models/api/preferencesModel';

/*
 * Types.
 */

enum DirectionsEnum {
  TOP = 'TOP',
  RIGHT = 'RIGHT',
  BOTTOM = 'BOTTOM',
  LEFT = 'LEFT',
}

/*
 * Props.
 */

interface InfoBubbleHighlightProps {
  anchor: Anchor;
  infoBubbleType: OnboardingTipTypesEnum;
  onOutsideHighlightClick?: Handler;
}

/*
 * Style.
 */

interface HighlightPulseWrapperStyleProps {
  $shouldWrapTight: boolean;
}
const StyledHighlightPulseWrapperDiv = styled.div<HighlightPulseWrapperStyleProps>`
  width: 100%;
  height: 100%;
  overflow: visible;
  position: relative;
  ${(p) => getHighlightPulseCss(p.$shouldWrapTight)};
`;

interface PointerBlockerStyleProps {
  $direction: DirectionsEnum;
}
const StyledPointerBlockerDiv = styled.div<PointerBlockerStyleProps>`
  position: absolute;
  pointer-events: all;
  ${(p) => addBlockerDirectionStyle(p.$direction)}
`;

function addBlockerDirectionStyle(direction: DirectionsEnum) {
  switch (direction) {
    case DirectionsEnum.TOP:
      return css`
        left: 0;
        right: 0;
        top: -100000px;
        height: 100000px;
      `;

    case DirectionsEnum.BOTTOM:
      return css`
        left: 0;
        right: 0;
        bottom: -100000px;
        height: 100000px;
      `;

    case DirectionsEnum.LEFT:
      return css`
        top: -100000px;
        left: -100000px;
        width: 100000px;
        height: 200000px;
      `;

    case DirectionsEnum.RIGHT:
    default:
      return css`
        top: -100000px;
        right: -100000px;
        width: 100000px;
        height: 200000px;
      `;
  }
}

/*
 * Component.
 */

export const InfoBubbleHighlight: FC<InfoBubbleHighlightProps> = ({
  anchor,
  infoBubbleType,
  onOutsideHighlightClick,
}) => {
  const shouldUseTightHighlightStyle =
    infoBubbleType === OnboardingTipTypesEnum.GUIDED_DEMO_COMMENT_COMPOSER ||
    infoBubbleType === OnboardingTipTypesEnum.START_GUIDE_COMMENT_COMPOSER ||
    infoBubbleType === OnboardingTipTypesEnum.COMMENT_COMPOSER ||
    infoBubbleType === OnboardingTipTypesEnum.PRIVATE_INBOX_COMMENT;

  return (
    <Layer>
      <RepositionOverlay anchor={anchor} isInteractive={false}>
        <StyledHighlightPulseWrapperDiv $shouldWrapTight={shouldUseTightHighlightStyle} />
        {onOutsideHighlightClick && renderPointerBlockers(onOutsideHighlightClick)}
      </RepositionOverlay>
    </Layer>
  );
};

// Render rectangles in each direction of the target to prevent clicks outside of it.
function renderPointerBlockers(onOutsideHighlightClick?: Handler) {
  return collectEnumValues(DirectionsEnum).map((p) => (
    <StyledPointerBlockerDiv key={p} $direction={p} onClick={onOutsideHighlightClick} />
  ));
}
